import { create } from 'zustand';
import type { ModalAssetItem } from '~/features/chat/modal-asset';

/**
 * Where every "open a chat / Build / Settings" call (`window-payloads.ts`)
 * lands. `OverlayHost` (`overlay-host.tsx`) renders the chat or Build as ONE
 * full-screen overlay at a time (opening a Build closes the chat and the other
 * way round — "modal viewers are mutually exclusive, never stacked"); Settings
 * is the `/settings` route modal.
 *
 * `navigateTo` carries a route change for callers that have no router (a
 * store function asked to open Settings); the host consumes it.
 */
export interface OverlayChatOpen {
  item: ModalAssetItem;
  /** The page the request was raised from; closing navigates back to it (FRONT-1550). */
  returnTo: string | null;
}

export interface OverlayBuildOpen {
  /** `undefined`: resume the most recent build, or start one. */
  sessionId: string | undefined;
  returnTo: string | null;
}

interface OverlayOpenState {
  chat: OverlayChatOpen | null;
  build: OverlayBuildOpen | null;
  navigateTo: string | null;
  openChat: (item: ModalAssetItem, returnTo: string | null) => void;
  openBuild: (sessionId: string | undefined, returnTo: string | null) => void;
  requestNavigation: (path: string) => void;
  clearChat: () => void;
  clearBuild: () => void;
  clearNavigation: () => void;
}

export const useOverlayOpenStore = create<OverlayOpenState>((set) => ({
  chat: null,
  build: null,
  navigateTo: null,
  // FRONT-1550: a request raised from "home" carries null; it must not
  // downgrade the origin of a window that is already open (same rule the old
  // chat launcher had), so the previous `returnTo` wins over null.
  openChat: (item, returnTo) =>
    set((s) => ({ chat: { item, returnTo: returnTo ?? s.chat?.returnTo ?? null }, build: null })),
  openBuild: (sessionId, returnTo) =>
    set((s) => ({ build: { sessionId, returnTo: returnTo ?? s.build?.returnTo ?? null }, chat: null })),
  requestNavigation: (path) => set({ navigateTo: path }),
  clearChat: () => set({ chat: null }),
  clearBuild: () => set({ build: null }),
  clearNavigation: () => set({ navigateTo: null }),
}));

/** Home's two addresses — where the overlays live. */
const HOME_PATHS: ReadonlySet<string> = new Set(['/', '/chat']);

/**
 * Closes the open overlay in place when the page changes to anything but Home.
 * The overlays live on Home (the openers' "Home detour" lands there, so that
 * change keeps them open); leaving it — Back, the sidebar, the command palette —
 * closes them.
 */
export function shouldCloseOnPageChange(open: boolean, previous: string, next: string): boolean {
  return open && previous !== next && !HOME_PATHS.has(next);
}

/** Test helper. */
export function resetOverlayOpenStore(): void {
  useOverlayOpenStore.setState({ chat: null, build: null, navigateTo: null });
}
